import { Link } from "react-router-dom";
import type { Movie } from "@/mocks/movies";
import { categoryLabels } from "@/mocks/movies";
import { useFavorites } from "@/hooks/useFavorites";

interface MovieCardProps {
  movie: Movie;
  showFavorite?: boolean;
}

export default function MovieCard({ movie, showFavorite = true }: MovieCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(movie.id);

  const handleFavorite = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(movie.id);
  };

  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block overflow-hidden rounded-lg border border-background-200/70 bg-background-100 transition-colors duration-300 hover:border-accent-500/60"
    >
      <div className="relative h-[300px] w-full overflow-hidden sm:h-[330px]">
        <img
          src={movie.poster}
          alt={`${movie.title}海报`}
          title={`${movie.title} - 91在线视频高清影视`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/10 to-transparent" />
        <span className="absolute left-2 top-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-xs font-600 text-background-50">
          {categoryLabels[movie.category]}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-accent-500/95 px-2 py-0.5 text-xs font-700 text-background-50">
          <i className="ri-star-fill text-[10px]" />
          {movie.rating.toFixed(1)}
        </span>
        {showFavorite && (
          <button
            type="button"
            onClick={handleFavorite}
            aria-label={favorited ? "取消收藏" : "收藏"}
            className={`absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full backdrop-blur-sm transition-colors ${
              favorited
                ? "bg-accent-500 text-background-50"
                : "bg-background-50/70 text-foreground-700 hover:text-accent-500"
            }`}
          >
            <i className={favorited ? "ri-heart-fill text-base" : "ri-heart-line text-base"} />
          </button>
        )}
        <div className="absolute inset-x-0 bottom-0 p-3">
          <h3 className="line-clamp-1 text-sm font-600 text-foreground-950 transition-colors group-hover:text-accent-500">
            {movie.title}
          </h3>
          <div className="mt-1 flex items-center gap-2 text-xs text-foreground-500">
            <span>{movie.year}</span>
            <span className="h-1 w-1 rounded-full bg-foreground-400" />
            <span className="line-clamp-1">{movie.genres.join(" / ")}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2.5 text-xs text-foreground-500">
        <span className="line-clamp-1">{movie.region}</span>
        <span className="flex items-center gap-1 text-foreground-600">
          <i className="ri-fire-line text-accent-500" />
          {movie.views}万
        </span>
      </div>
    </Link>
  );
}